import { Country } from '@dreryk/address-common-api';
import { VisitMedicalService, VisitSettlement } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { FiscalDocument } from '@dreryk/egabinet-invoice-frontend-api';
import { Patient } from '@dreryk/egabinet-patient-frontend-api';

import { InvoiceCreateFormAddressSectionBeanPart } from './addressSection/InvoiceCreateFormAddressSection.types';
import { InvoiceCreateFormBean } from './InvoiceCreateForm.types';
import { InvoiceCreateFormPrivateIndividualSectionBeanPart } from './privateIndividualSection/InvoiceCreateFormPrivateIndividualSection.types';
import { InvoiceItemsTableBean } from './servicesSection/InvoiceItemsTable.types';
import { addEmptyRow, mapMedicalServiceTaxRateCodeToInvoiceTaxRate } from './servicesSection/InvoiceItemsTable.utils';

const mapVisitMedicalServiceToInvoiceItemsTableBean = (
   visitMedicalService: VisitMedicalService[]
): InvoiceItemsTableBean[] =>
   visitMedicalService.length > 0
      ? visitMedicalService.map((item) => ({
           invoiceParameters: {
              nameEditable: false,
              nameForInvoicing: item.medicalService.nameForInvoicing,
              nameForReceipt: item.medicalService.nameForReceipt,
              rowAddedManually: false,
              unitDefaultPrice: item.price.asNumber(),
           },
           name: item.medicalService.name,
           quantity: 1,
           taxRate: mapMedicalServiceTaxRateCodeToInvoiceTaxRate(item.medicalService.taxRateCode),
           unit: 'SERVICE',
           unitPrice: item.price.asNumber(),
        }))
      : [addEmptyRow()];

export const getInvoiceCreateFormInitialValues = (
   patient: Patient,
   visitSettlement: VisitSettlement
): Partial<InvoiceCreateFormBean> => ({
   building: patient.address?.findBuilding(),
   country: patient.address?.getCountry(),
   firstName: patient.firstName ?? null,
   flat: patient.address?.findFlat(),
   invoiceItems: mapVisitMedicalServiceToInvoiceItemsTableBean(visitSettlement.medicalServices),
   lastName: patient.lastName ?? null,
   paymentDate: visitSettlement.paymentDate,
   postcode: patient.address?.findPostcode(),
   street: patient.address?.findStreet(),
   taxExemptionBasis: 'EXEMPTION_18',
   town: patient.address?.findTown(),
   description: undefined,
});

export const getCompanyDefaultValues = (): InvoiceCreateFormAddressSectionBeanPart => ({
   building: null,
   country: Country.DEFAULT,
   flat: null,
   postcode: null,
   street: null,
   town: null,
});

export const getPrivateInvidualDefaultValues = (
   patient: Patient
): Partial<InvoiceCreateFormAddressSectionBeanPart & InvoiceCreateFormPrivateIndividualSectionBeanPart> => ({
   building: patient.address?.findBuilding(),
   country: patient.address?.getCountry(),
   firstName: patient.firstName ?? null,
   flat: patient.address?.findFlat(),
   lastName: patient.lastName ?? null,
   postcode: patient.address?.findPostcode(),
   street: patient.address?.findStreet(),
   town: patient.address?.findTown(),
});

type InvoiceTypeVisitor = <ACCEPT>(visitor: { withReceipt: () => ACCEPT; default: () => ACCEPT }) => ACCEPT;

export const invoiceTypeVisitor = (fiscalPrintEnabled: boolean): InvoiceTypeVisitor => (visitor) => {
   return fiscalPrintEnabled ? visitor.withReceipt() : visitor.default();
};

type FiscalDocumentTypeVisitor = <ACCEPT>(visitor: {
   receiptWithTaxIdentifier?: () => ACCEPT;
   receiptWithoutTaxIdentifier?: () => ACCEPT;
   invoiceWithTaxIdentifier?: () => ACCEPT;
   invoiceWithoutTaxIdentifier?: () => ACCEPT;
   noFiscalDocuments: () => ACCEPT;
}) => ACCEPT;

export const fiscalDocumentTypeVisitor = (fiscalDocuments: FiscalDocument[] | undefined): FiscalDocumentTypeVisitor => (
   visitor
) => {
   if (fiscalDocuments?.some((item) => item.type === 'RECEIPT' && item.taxIdentifier)) {
      return visitor.receiptWithTaxIdentifier?.() ?? visitor.noFiscalDocuments();
   }
   if (fiscalDocuments?.some((item) => item.type === 'RECEIPT' && !item.taxIdentifier)) {
      return visitor.receiptWithoutTaxIdentifier?.() ?? visitor.noFiscalDocuments();
   }
   if (fiscalDocuments?.some((item) => item.type === 'INVOICE' && item.taxIdentifier)) {
      return visitor.invoiceWithTaxIdentifier?.() ?? visitor.noFiscalDocuments();
   }
   if (fiscalDocuments?.some((item) => item.type === 'INVOICE' && !item.taxIdentifier)) {
      return visitor.invoiceWithoutTaxIdentifier?.() ?? visitor.noFiscalDocuments();
   }
   return visitor.noFiscalDocuments();
};
